/**
 * The words on the spreadsheet change card, and the rules that pick them.
 *
 * Split from `AgentSpreadsheetChangeCard` the way `toolConfirmationCopy` is
 * split from its prompt: this is the part that must tell the truth about the
 * workbook (rule 12), so it lives where it can be read on its own.
 */

import {
  SPREADSHEET_CHANGE_OUTCOMES,
  SPREADSHEET_REFUSED_REASONS,
} from '@bower/shared/spreadsheet/result';
import type {
  SpreadsheetChange,
  SpreadsheetChangeOutcome,
  SpreadsheetChangeReason,
} from '@bower/shared';

import type { SpreadsheetChangeCardData } from '../../../services/agentChat.service';

interface OutcomeCopy {
  readonly heading: string;
  readonly icon: string;
  readonly color: string;
}

/** Group rank on a failed card: what is in the sheet first, then what is not. */
const OUTCOME_RANK: Readonly<Record<SpreadsheetChangeOutcome, number>> = {
  landed: 0,
  failed: 1,
  unknown: 2,
  not_attempted: 3,
};

/** Every shared outcome, in group order, so none can be left out of the card. */
export const OUTCOME_ORDER: readonly SpreadsheetChangeOutcome[] = [
  ...SPREADSHEET_CHANGE_OUTCOMES,
].sort((a, b) => OUTCOME_RANK[a] - OUTCOME_RANK[b]);

export const OUTCOME_COPY: Readonly<Record<SpreadsheetChangeOutcome, OutcomeCopy>> = {
  landed: { heading: 'Applied, still in the sheet', icon: 'check_circle', color: 'text.success' },
  // `failed` means applied or attempted and the read-back said no, so the
  // change may be partly in the sheet: never say it "did not apply".
  failed: { heading: "Didn't come out as expected", icon: 'error', color: 'text.error' },
  unknown: { heading: 'May not have saved', icon: 'help', color: 'text.warning' },
  not_attempted: { heading: 'Not attempted', icon: 'block', color: 'text.muted' },
};

/**
 * An outcome this build does not know (the enum grew after it shipped). The
 * change may be in the sheet, so it is shown, never dropped, and claims
 * nothing about its state.
 */
export const OTHER_OUTCOME_COPY: OutcomeCopy = {
  heading: 'Check this in the spreadsheet',
  icon: 'help',
  color: 'text.warning',
};

export function outcomeCopy(outcome: string): OutcomeCopy {
  return Object.hasOwn(OUTCOME_COPY, outcome)
    ? OUTCOME_COPY[outcome as SpreadsheetChangeOutcome]
    : OTHER_OUTCOME_COPY;
}

/** "Sheet1!A1:B9", or whichever half the op has. */
export function location(change: SpreadsheetChange): string | null {
  if (change.sheet && change.range) return `${change.sheet}!${change.range}`;
  return change.sheet ?? change.range ?? null;
}

/**
 * A batch is failed when the result says so OR any change did not land. The
 * backend sets `applied` only when every op landed, but the card is the
 * researcher's record of the workbook, so it trusts the per-change outcomes
 * over a summary flag that could disagree with them.
 */
export function isFailed(card: SpreadsheetChangeCardData): boolean {
  return card.status === 'failed' || card.changes.some((c) => c.outcome !== 'landed');
}

/**
 * Failure reasons the backend raises BEFORE it mutates anything, from the one
 * shared list the backend summary also reads, so the headline and the summary
 * agree. Every other reason may leave part of the change behind.
 */
const REFUSED_BEFORE_WRITING: ReadonlySet<SpreadsheetChangeReason> = new Set(
  SPREADSHEET_REFUSED_REASONS,
);

function leftTheSheetAlone(change: SpreadsheetChange): boolean {
  if (change.outcome === 'not_attempted') return true;
  return (
    change.outcome === 'failed' && !!change.reason && REFUSED_BEFORE_WRITING.has(change.reason)
  );
}

/**
 * Only claim nothing changed when every change provably left the sheet alone
 * (rule 12): a `failed` change may be partly in the workbook.
 */
export function headline(card: SpreadsheetChangeCardData, failed: boolean): string {
  if (!failed) return 'Spreadsheet updated';
  if (card.changes.every(leftTheSheetAlone)) return 'No changes were made';
  if (card.changes.every((c) => c.outcome === 'unknown')) return 'Changes may not have saved';
  return 'Some changes need attention';
}

/** The Undo action's words (phase 3). The refusal messages come from the backend. */
export const UNDO_COPY = {
  undo: 'Undo',
  undoing: 'Undoing',
  undone: 'Undone',
  history: 'Open version history',
  // No answer, or a server fault that is not a clean 503: the outcome is unknown.
  unknownOutcome:
    'Bower could not finish the undo, so it may or may not have happened. Check the spreadsheet before you try again.',
  // A retry-safe refusal of an undo the agent ran from chat: nothing changed.
  retryLater:
    'Bower could not undo the change just now. Nothing was changed; try again in a moment.',
} as const;

/**
 * Final refusals whose server message ends "Use version history ...": the
 * token ones (`agent-undo.service.ts` UNDO_MESSAGES) and the two engine
 * refusals no retry can fix (its `engineError`, from `engineFailure`). The
 * other final refusals (NOT_YOURS, ENTITY_ACCESS_DENIED, not found, importing,
 * not configured) do not recommend history, so they do not offer it.
 */
const HISTORY_REFUSALS: ReadonlySet<string> = new Set([
  'SPREADSHEET_UNDO_INVALID',
  'SPREADSHEET_UNDO_EXPIRED',
  'SPREADSHEET_UNSUPPORTED_FEATURE',
  'SPREADSHEET_TOO_LARGE',
]);

/** What the card does after a failed undo. */
export interface UndoFailurePlan {
  /** Offer "Open version history". */
  readonly offerHistory: boolean;
  /** Keep Undo available: a second press is safe and may succeed. */
  readonly canRetry: boolean;
  /** Show this instead of the server's message, which cannot know the outcome. */
  readonly message?: string;
  /**
   * The undo may have happened (UNCONFIRMED, or no clean answer at all), so a
   * later plain refusal for the same token must not replace this outcome.
   */
  readonly mayHaveHappened: boolean;
}

/**
 * Only what the code (or the lack of any response) proves. EDITED_SINCE left
 * the unit alone; UNCONFIRMED may have reverted it; a 503 (busy, engine)
 * changed nothing and a retry is safe. No response, or any other server fault,
 * means the outcome is unknown: a second press answers truthfully
 * (EDITED_SINCE if the first one landed). The grid reloads after EVERY outcome
 * (a suspended grid is unmounted, so its resume is a fresh mount), so the plan
 * has no say in that. Every other refusal (a 4xx
 * the route chose) is final; it offers version history wherever its message
 * recommends it: HISTORY_REFUSALS, and a refusal with no message of its own
 * (the card's neutral fallback says to use version history).
 */
export function undoFailurePlan(
  code: string,
  status: number | undefined,
  hasServerMessage = true,
): UndoFailurePlan {
  if (code === 'SPREADSHEET_EDITED_SINCE') {
    return { offerHistory: true, canRetry: false, mayHaveHappened: false };
  }
  if (code === 'SPREADSHEET_UNDO_UNCONFIRMED') {
    return { offerHistory: true, canRetry: false, mayHaveHappened: true };
  }
  if (status === 503) return { offerHistory: false, canRetry: true, mayHaveHappened: false };
  if (status === undefined || status >= 500) {
    return {
      offerHistory: true,
      canRetry: true,
      message: UNDO_COPY.unknownOutcome,
      mayHaveHappened: true,
    };
  }
  const recommendsHistory = HISTORY_REFUSALS.has(code) || !hasServerMessage;
  return { offerHistory: recommendsHistory, canRetry: false, mayHaveHappened: false };
}

/**
 * The spreadsheet codes the backend answers with 503, mirrored from
 * `packages/backend/src/utils/controllerHelpers.ts` (`getStatusCodeFromError`):
 * our side was unavailable or the unit was busy, nothing was changed, and a
 * retry is safe. Keep in step with that map.
 */
const RETRY_SAFE_CODES: ReadonlySet<string> = new Set([
  'SPREADSHEET_BUSY',
  'SPREADSHEET_ENGINE_UNAVAILABLE',
  'SPREADSHEETS_NOT_CONFIGURED',
]);

/**
 * The plan for an undo the agent ran from chat (`spreadsheet_undo`). That
 * frame carries the refusal's code only, with no message and no HTTP status,
 * so it is read as the route would answer it: a retry-safe code (503) keeps
 * Undo with a retry message, every other code is a final 4xx with no message
 * of its own. UNCONFIRMED keeps its meaning (it may have happened) through
 * the card's unknown-outcome copy, where the neutral fallback would say it
 * did not happen.
 */
export function agentUndoFailurePlan(code: string): UndoFailurePlan {
  if (RETRY_SAFE_CODES.has(code)) {
    return { ...undoFailurePlan(code, 503, false), message: UNDO_COPY.retryLater };
  }
  const plan = undoFailurePlan(code, 400, false);
  return code === 'SPREADSHEET_UNDO_UNCONFIRMED'
    ? { ...plan, message: UNDO_COPY.unknownOutcome }
    : plan;
}
